import { SectionHeader } from '@/design'
import { useCurrentTenant, useSession } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'

import { AiSummaryCard } from './AiSummaryCard'
import { AlertsCard } from './AlertsCard'
import { FunnelCard } from './FunnelCard'
import { useKpis } from './hooks'
import { KpiGrid } from './KpiGrid'
import { MarketingPerformanceCard } from './MarketingPerformanceCard'
import { RevenueTrendCard } from './RevenueTrendCard'
import { TopCompetitionsCard } from './TopCompetitionsCard'
import './overview.css'

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/**
 * The command Overview: "what do I need to know right now?". Headline figures,
 * a written summary and what needs attention first; then how revenue, the
 * funnel, competitions and channels got there. Every tile, row and alert is a
 * link to the page that explains it, carrying the filters with it.
 */
export function OverviewPage() {
  const tenant = useCurrentTenant()
  const session = useSession()
  const kpis = useKpis()
  const period = kpis.data?.meta.period
  // The page's one freshness declaration: the KPIs' own provenance ("Live data · Last updated …" on the live client).
  usePageFreshness(kpis.data?.meta)
  const firstName = session.data?.name?.split(' ')[0]

  const description = [
    firstName ? `${greeting(new Date().getHours())}, ${firstName}.` : null,
    tenant.data && period
      ? `How ${tenant.data.name} is performing · ${period.label}${period.compareLabel ? ` ${period.compareLabel}` : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="ov-page">
      <SectionHeader title="Overview" description={description || undefined} />
      <div className="ov-layout">
        <KpiGrid />
        <div className="ov-columns">
          <div className="ov-column">
            <AiSummaryCard />
            <RevenueTrendCard />
          </div>
          <div className="ov-column">
            <AlertsCard />
            <FunnelCard />
          </div>
        </div>
        <TopCompetitionsCard />
        <MarketingPerformanceCard />
      </div>
    </div>
  )
}
